import { measureFontText, getHeadingFont } from "./graphicFonts";
import { TextSpec, pct } from "./layout";

/** Heading face used on the stamp (preview + PNG export). */
export const GRAPHIC_HEADING_FONT = "Castledown";

export interface ResolvedSignLayout {
  fontPct: number;
  y: number;
  lines: string[];
}

export const measureHeadingText = (
  text: string,
  _fontWeight: number,
  fontSizePx: number
): number => {
  try {
    return measureFontText(getHeadingFont(), text, fontSizePx);
  } catch {
    // Fonts not loaded yet (shouldn't happen during render)
    return text.length * fontSizePx * 0.55;
  }
};

/** Prefer a balanced 2-line split; fall back to greedy wrap. */
const wrapSignLines = (
  text: string,
  maxWidth: number,
  maxLines: number,
  measure: (line: string) => number
): string[] => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];
  if (words.length === 1 || maxLines <= 1) return [words.join(" ")];

  if (maxLines === 2 && words.length >= 2) {
    let best: string[] | null = null;
    let bestScore = Infinity;
    for (let i = 1; i < words.length; i += 1) {
      const a = words.slice(0, i).join(" ");
      const b = words.slice(i).join(" ");
      const wa = measure(a);
      const wb = measure(b);
      if (wa <= maxWidth && wb <= maxWidth) {
        const score = Math.max(wa, wb) + Math.abs(wa - wb) * 0.25;
        if (score < bestScore) {
          bestScore = score;
          best = [a, b];
        }
      }
    }
    if (best) return best;
  }

  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (measure(candidate) <= maxWidth || !current) {
      current = candidate;
    } else if (lines.length < maxLines - 1) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, maxLines);
};

/**
 * If signage fits on one line at the base size, keep it.
 * Otherwise shrink font, shift up, and wrap (see `spec.overflow`).
 * Requires loadGraphicFonts() first so widths match the path renderer.
 */
export const resolveSignTextLayout = (
  text: string,
  spec: TextSpec,
  stampW: number
): ResolvedSignLayout => {
  const maxW = pct(spec.maxW, stampW);
  const baseSize = pct(spec.fontPct, stampW);
  const fits =
    measureHeadingText(text, spec.fontWeight, baseSize) <= maxW + 0.5;

  if (fits || !spec.overflow) {
    return { fontPct: spec.fontPct, y: spec.y, lines: [text] };
  }

  const fontPct = spec.fontPct * spec.overflow.fontScale;
  const fontSize = pct(fontPct, stampW);
  const lines = wrapSignLines(text, maxW, spec.overflow.maxLines, (line) =>
    measureHeadingText(line, spec.fontWeight, fontSize)
  );

  return {
    fontPct,
    y: spec.y + spec.overflow.yShiftPct,
    lines,
  };
};
